/**
 * Central orchestrator for the 4x3 grid of simulations
 */

import type { Scenario } from '../types/scenario';
import type { SimulationState, CellPosition } from '../types/simulation';
import { Simulation } from './Simulation';
import { cellIdFromPosition } from '../types/simulation';

export interface SimulationManagerEvents {
  onActiveChange?: (cellId: string | null) => void;
  onSimulationStateChange?: (cellId: string, state: SimulationState) => void;
  onError?: (cellId: string, error: Error) => void;
}

export class SimulationManager {
  private simulations: Map<string, Simulation> = new Map();
  private _activeId: string | null = null;
  private events: SimulationManagerEvents;

  readonly rows: number;
  readonly cols: number;

  constructor(
    rows: number = 3,
    cols: number = 4,
    events: SimulationManagerEvents = {}
  ) {
    this.rows = rows;
    this.cols = cols;
    this.events = events;
  }

  get activeId(): string | null {
    return this._activeId;
  }

  /**
   * Initialize a cell's simulation with its canvas
   */
  async initializeCell(
    position: CellPosition,
    canvas: HTMLCanvasElement
  ): Promise<void> {
    const id = cellIdFromPosition(position);

    if (this.simulations.has(id)) {
      throw new Error(`Cell ${id} already initialized`);
    }

    const sim = new Simulation(id, {
      onStateChange: (state) => {
        this.events.onSimulationStateChange?.(id, state);
      },
      onError: (error) => {
        this.events.onError?.(id, error);
      },
    });

    this.simulations.set(id, sim);

    try {
      await sim.initialize(canvas);
    } catch (error) {
      this.simulations.delete(id);
      throw error;
    }
  }

  /**
   * Get simulation by cell position
   */
  getSimulation(position: CellPosition): Simulation | undefined {
    const id = cellIdFromPosition(position);
    return this.simulations.get(id);
  }

  /**
   * Get simulation by cell ID
   */
  getSimulationById(id: string): Simulation | undefined {
    return this.simulations.get(id);
  }

  /**
   * Set the active cell
   */
  setActive(position: CellPosition | null): void {
    const newId = position ? cellIdFromPosition(position) : null;

    if (newId === this._activeId) {
      return;
    }

    this._activeId = newId;
    this.events.onActiveChange?.(newId);
  }

  /**
   * Set active by ID
   */
  setActiveById(id: string | null): void {
    if (id === this._activeId) {
      return;
    }

    if (id && !this.simulations.has(id)) {
      return;
    }

    this._activeId = id;
    this.events.onActiveChange?.(id);
  }

  /**
   * Get the currently active simulation
   */
  getActive(): Simulation | null {
    if (!this._activeId) {
      return null;
    }
    return this.simulations.get(this._activeId) ?? null;
  }

  /**
   * Load scenario into active cell
   */
  async loadScenarioToActive(scenario: Scenario): Promise<void> {
    const active = this.getActive();
    if (!active) {
      throw new Error('No active cell');
    }
    await active.loadScenario(scenario);
  }

  /**
   * Load scenario into specific cell
   */
  async loadScenario(position: CellPosition, scenario: Scenario): Promise<void> {
    const sim = this.getSimulation(position);
    if (!sim) {
      throw new Error(`Cell ${cellIdFromPosition(position)} not initialized`);
    }
    await sim.loadScenario(scenario);
  }

  /**
   * Play all simulations
   */
  playAll(): void {
    for (const sim of this.simulations.values()) {
      if (sim.isReady) {
        sim.play();
      }
    }
  }

  /**
   * Pause all simulations
   */
  pauseAll(): void {
    for (const sim of this.simulations.values()) {
      if (sim.isRunning) {
        sim.pause();
      }
    }
  }

  /**
   * Play active simulation
   */
  playActive(): void {
    this.getActive()?.play();
  }

  /**
   * Pause active simulation
   */
  pauseActive(): void {
    this.getActive()?.pause();
  }

  /**
   * Step active simulation
   */
  stepActive(): void {
    this.getActive()?.step();
  }

  /**
   * Get all simulation states
   */
  getAllStates(): Map<string, SimulationState> {
    const states = new Map<string, SimulationState>();
    for (const [id, sim] of this.simulations) {
      states.set(id, sim.getState());
    }
    return states;
  }

  /**
   * Resize a specific cell's renderer
   */
  resizeCell(position: CellPosition, width: number, height: number): void {
    this.getSimulation(position)?.resize(width, height);
  }

  /**
   * Resize all cells
   */
  resizeAllCells(getCellSize: (pos: CellPosition) => { width: number; height: number }): void {
    for (let row = 0; row < this.rows; row++) {
      for (let col = 0; col < this.cols; col++) {
        const pos = { row, col };
        const sim = this.getSimulation(pos);
        if (sim) {
          const { width, height } = getCellSize(pos);
          sim.resize(width, height);
        }
      }
    }
  }

  /**
   * Clean up all simulations
   */
  disposeAll(): void {
    for (const sim of this.simulations.values()) {
      sim.dispose();
    }
    this.simulations.clear();
    this._activeId = null;
  }

  /**
   * Dispose a specific cell
   */
  disposeCell(position: CellPosition): void {
    const id = cellIdFromPosition(position);
    const sim = this.simulations.get(id);
    if (sim) {
      sim.dispose();
      this.simulations.delete(id);
      if (this._activeId === id) {
        this._activeId = null;
        this.events.onActiveChange?.(null);
      }
    }
  }

  /**
   * Check if a cell is initialized
   */
  isCellInitialized(position: CellPosition): boolean {
    return this.simulations.has(cellIdFromPosition(position));
  }

  /**
   * Get count of initialized cells
   */
  get initializedCount(): number {
    return this.simulations.size;
  }
}
